import { Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { WebhookService } from '../../services/webhook.service';
import { WebhookDTO, WebhookEventDTO, WebhookIntentDTO } from '../../core/models';
import { WebhookReadComponent } from './webhook-read.component';

@Component({
  selector: 'app-webhook-list',
  templateUrl: './webhook-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .webhooks-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .webhooks-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .webhooks-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .webhooks-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .webhook-details-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .webhook-details-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [
    CommonModule,
    PERMISSION_UI_DIRECTIVES,
    RouterLink,
    TableModule,
    ButtonModule,
    CardModule,
    DrawerModule,
    TagModule,
    TooltipModule,
    MenuModule,
    WebhookReadComponent
  ]
})
export class WebhookListComponent implements OnInit, OnDestroy {
  private webhookService = inject(WebhookService);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  @ViewChild('rowMenu') rowMenu!: Menu;

  activeTab = signal<'endpoints' | 'events'>('endpoints');

  webhooks = signal<WebhookDTO[]>([]);
  webhooksLoading = signal(false);

  events = signal<WebhookEventDTO[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  currentPage = signal(0);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];

  sidebarVisible = signal(false);
  webhookDetailsOpen = signal(false);
  selectedWebhookDetail = signal<WebhookDTO | null>(null);
  webhookDetailsLoading = signal(false);
  selectedEvent = signal<WebhookEventDTO | null>(null);
  intents = signal<WebhookIntentDTO[]>([]);
  intentsLoading = signal(false);
  resendLoading = signal(false);
  autoRefreshEnabled = signal(false);
  private autoRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private readonly AUTO_REFRESH_MS = 10_000;

  rowMenuItems: MenuItem[] = [];
  private selectedWebhook: WebhookDTO | null = null;

  ngOnInit(): void {
    this.loadWebhooks();
  }

  ngOnDestroy(): void {
    this.clearAutoRefresh();
  }

  setTab(tab: 'endpoints' | 'events'): void {
    this.activeTab.set(tab);
    if (tab === 'events' && this.events().length === 0) {
      this.loadEvents({ first: 0, rows: this.pageSize() });
    }
  }

  loadWebhooks(): void {
    this.webhooksLoading.set(true);
    this.webhookService.listWebhooks()
      .pipe(finalize(() => this.webhooksLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok) {
            this.webhooks.set(res.body || []);
          } else {
            this.toastr.error(res.message || 'Error al cargar webhooks');
          }
        },
        error: () => this.toastr.error('Error al cargar webhooks')
      });
  }

  openRowMenu(event: Event, webhook: WebhookDTO): void {
    this.selectedWebhook = webhook;
    this.rowMenuItems = [
      {
        label: 'Ver',
        icon: 'pi pi-eye',
        command: () => this.openWebhookDetails(this.selectedWebhook)
      },
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => {
          if (this.selectedWebhook?.id != null) {
            this.router.navigate(['/webhooks', this.selectedWebhook.id]);
          }
        }
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        command: () => this.deleteWebhook(this.selectedWebhook)
      }
    ];
    this.rowMenu.toggle(event);
  }

  deleteWebhook(webhook: WebhookDTO | null): void {
    if (!webhook?.id) return;
    if (!confirm(`¿Eliminar el webhook "${webhook.description || webhook.endpoint}"?`)) return;

    this.webhookService.deleteWebhook(webhook.id).subscribe({
      next: (res) => {
        if (res.ok) {
          this.toastr.success('Webhook eliminado');
          this.loadWebhooks();
        } else {
          this.toastr.error(res.message || 'No se pudo eliminar');
        }
      },
      error: (err) => this.toastr.error(err.error?.message || 'Error al eliminar')
    });
  }

  openWebhookDetails(webhook: WebhookDTO | null, webhookId?: number): void {
    const id = webhook?.id ?? webhookId;
    if (id == null) return;
    this.webhookDetailsOpen.set(true);
    this.selectedWebhookDetail.set(null);
    this.webhookDetailsLoading.set(true);
    this.webhookService.getWebhook(id).subscribe({
      next: (res) => {
        if (res.ok && res.body) {
          this.selectedWebhookDetail.set(res.body);
        } else {
          this.toastr.error(res.message || 'Error al cargar el webhook');
        }
        this.webhookDetailsLoading.set(false);
      },
      error: () => {
        this.toastr.error('Error al cargar el webhook');
        this.webhookDetailsLoading.set(false);
      }
    });
  }

  closeWebhookDetails(): void {
    this.webhookDetailsOpen.set(false);
    this.selectedWebhookDetail.set(null);
    this.webhookDetailsLoading.set(false);
  }

  onWebhookDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeWebhookDetails();
  }

  editWebhookFromDrawer(): void {
    const webhook = this.selectedWebhookDetail();
    if (webhook?.id == null) return;
    this.closeWebhookDetails();
    this.router.navigate(['/webhooks', webhook.id]);
  }

  eventCountLabel(webhook: WebhookDTO): string {
    const n = webhook.subscribedEvents?.length ?? 0;
    return n === 1 ? '1 evento' : `${n} eventos`;
  }

  refreshTable(): void {
    this.loadEvents({ first: this.currentPage() * this.pageSize(), rows: this.pageSize() });
  }

  toggleAutoRefresh(): void {
    const next = !this.autoRefreshEnabled();
    this.autoRefreshEnabled.set(next);
    if (next) {
      this.autoRefreshIntervalId = setInterval(() => this.refreshTable(), this.AUTO_REFRESH_MS);
    } else {
      this.clearAutoRefresh();
    }
  }

  private clearAutoRefresh(): void {
    if (this.autoRefreshIntervalId != null) {
      clearInterval(this.autoRefreshIntervalId);
      this.autoRefreshIntervalId = null;
    }
    this.autoRefreshEnabled.set(false);
  }

  loadEvents(event: { first?: number; rows?: number }): void {
    this.loading.set(true);
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize();
    const page = first / rows;

    this.webhookService.listEvents({}, { page, size: rows, sort: ['createdAt,desc'] })
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok) {
            this.events.set(res.body || []);
            this.totalRecords.set(res.page?.totalItems || 0);
            this.currentPage.set(page);
            this.pageSize.set(rows);
          } else {
            this.toastr.error(res.message || 'Error al cargar eventos');
          }
        },
        error: () => this.toastr.error('Error al cargar eventos')
      });
  }

  showDetails(event: WebhookEventDTO): void {
    this.selectedEvent.set(event);
    this.sidebarVisible.set(true);
    this.loadIntents(event.id!);
  }

  loadIntents(eventId: number): void {
    this.intentsLoading.set(true);
    this.webhookService.listIntents(eventId, { page: 0, size: 50, sort: ['createdAt,desc'] })
      .pipe(finalize(() => this.intentsLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok) {
            this.intents.set(res.body || []);
          } else {
            this.toastr.error('Error al cargar intentos');
          }
        },
        error: () => this.toastr.error('Error al cargar intentos')
      });
  }

  getStatusSeverity(status: string | undefined): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' | undefined {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'danger';
      case 'RETRYING': return 'info';
      default: return 'secondary';
    }
  }

  formatPayload(payload: string | undefined | null): string {
    if (payload == null || payload === '') return '';
    const trimmed = payload.trim();
    if (trimmed === '') return payload;
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return payload;
    }
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.sidebarVisible.set(visible);
    if (!visible) {
      this.selectedEvent.set(null);
    }
  }

  resendEvent(): void {
    const event = this.selectedEvent();
    if (!event?.id) return;
    this.resendLoading.set(true);
    this.webhookService.resendEvent(event.id)
      .pipe(finalize(() => this.resendLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.ok) {
            this.toastr.success('Evento reenviado correctamente. Se creó un nuevo evento en cola.');
            this.loadEvents({ first: this.currentPage() * this.pageSize(), rows: this.pageSize() });
          } else {
            this.toastr.error(res.message || 'Error al reenviar el evento');
          }
        },
        error: (err) => this.toastr.error(err.error?.message || 'Error al conectar con el servidor')
      });
  }
}
